/**
 * Web Brain — LLM inference for email parsing.
 *
 * PRIMARY: Groq (Llama 4 Scout, free) via Strapi backend brain proxy.
 * FALLBACK ON RATE LIMIT: Paid model via /meter/text (draws from AI credits wallet).
 * FALLBACK ON NO CREDITS: Throws InsufficientCreditsError → triggers upgrade modal.
 * LOCAL FALLBACK: In-browser Gemma via WebLLM (requires WebGPU, ~2.6GB download).
 *
 * The meter endpoint handles billing automatically:
 *   - Free models (Groq) → $0, no credits consumed
 *   - Paid models (Gemini Flash) → credits deducted from shared wallet
 *   - 402 → same upgrade/PAYG modal as Mittens
 */

const BACKEND_URL = process.env.REACT_APP_backend_baseUrl || 'http://localhost:1337';

// Paid fallback model when Groq is rate-limited
const PAID_FALLBACK_MODEL = 'gemini-flash';

// Brain mode: 'cloud' (default, Groq via backend) or 'local' (WebLLM)
let brainMode = 'cloud';
let localEngineInstance = null;
let localEngineReady = false;
let localEngineLoading = false;

/**
 * Get the auth token from localStorage (set by the app's auth system).
 */
function getAuthToken() {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || localStorage.getItem('jwt');
    return token;
  } catch {
    return null;
  }
}

/**
 * Check if WebGPU is supported in this browser.
 */
export function isWebGPUSupported() {
  return typeof navigator !== 'undefined' && 'gpu' in navigator;
}

/**
 * Get or create the brain instance.
 * @param {function} onProgress - Progress callback for local model download
 * @returns {Promise<object>} Brain with .text() method
 */
export async function getBrain(onProgress) {
  if (brainMode === 'cloud') {
    return getCloudBrain();
  }
  return getLocalBrain(onProgress);
}

/**
 * Set the brain mode.
 * @param {'cloud'|'local'} mode
 */
export function setBrainMode(mode) {
  brainMode = mode;
}

/**
 * Get current brain mode.
 */
export function getBrainMode() {
  return brainMode;
}

/**
 * Cloud brain — routes through the metered endpoint.
 *
 * Flow:
 *   1. Try Groq (free) via /meter/text → $0, no credits
 *   2. If Groq 429 rate limited → auto-fallback to Gemini Flash via /meter/text → draws AI credits
 *   3. If 402 insufficient credits → throw structured error for upgrade modal
 */
function getCloudBrain() {
  return {
    name: 'Groq Llama 4 Scout (cloud)',
    isLocal: false,

    async text(prompt, opts = {}) {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in first.');
      }

      // Try free model first
      try {
        return await callMeterText(token, prompt, 'groq-free', opts);
      } catch (err) {
        // 402 from Groq shouldn't happen (it's free), but handle it
        if (err.reason === 'insufficient_credits') throw err;

        // 429 or other Groq failure → fallback to paid model
        const is429 = err.message?.includes('429') || err.message?.includes('rate limit');
        if (is429) {
          console.warn('[WebBrain] Groq rate limited, falling back to paid model:', PAID_FALLBACK_MODEL);
          return await callMeterText(token, prompt, PAID_FALLBACK_MODEL, opts);
          // ↑ This may throw 402 if user has no AI credits → caught by email importer
        }

        // Other errors: re-throw
        throw err;
      }
    }
  };
}

/**
 * Call the metered brain endpoint.
 * @returns {string} Response text
 * @throws {{ reason: 'insufficient_credits', needed, balance, options, requestId }} on 402
 */
async function callMeterText(authToken, prompt, aiModel, opts = {}) {
  const res = await fetch(`${BACKEND_URL}/mittens-pipeline/meter/text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({
      prompt,
      aiModel,
      product: 'susu_closet',
      feature: 'order_extraction',
      opts: {
        temperature: opts.temperature ?? 0.1,
        maxTokens: opts.maxTokens ?? 1024,
        timeout: opts.timeout ?? 30000
      }
    })
  });

  // 402 insufficient credits → throw structured error for upgrade modal
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const err = new Error('Insufficient AI credits');
    err.reason = 'insufficient_credits';
    err.needed = body.needed || 0;
    err.balance = body.balance || 0;
    err.options = body.options || ['upgrade', 'payg_topup'];
    err.requestId = body.requestId;
    throw err;
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Brain API error (${res.status}): ${errText.slice(0, 200)}`);
  }

  const data = await res.json();
  return data.text || '';
}

/**
 * Local brain — Gemma via WebLLM (optional fallback).
 * Downloads ~2.6GB model on first use. Requires WebGPU.
 */
async function getLocalBrain(onProgress) {
  if (localEngineReady && localEngineInstance) {
    return wrapLocalEngine(localEngineInstance);
  }

  if (localEngineLoading) {
    return new Promise((resolve, reject) => {
      const check = setInterval(() => {
        if (localEngineReady && localEngineInstance) {
          clearInterval(check);
          resolve(wrapLocalEngine(localEngineInstance));
        }
        if (!localEngineLoading && !localEngineReady) {
          clearInterval(check);
          reject(new Error('Local engine load failed'));
        }
      }, 200);
    });
  }

  localEngineLoading = true;

  try {
    const webllm = await import('@mlc-ai/web-llm');
    const MODEL_ID = 'gemma-3-4b-it-q4f16_1-MLC'; // Using Gemma 3 4B as Gemma 4 not yet on WebLLM

    const engine = await webllm.CreateMLCEngine(MODEL_ID, {
      initProgressCallback: (report) => {
        const progress = Math.min(Math.round(report.progress * 100), 100);
        const isDownloading = report.text?.toLowerCase().includes('download') ||
                              report.text?.toLowerCase().includes('fetch');
        if (onProgress) {
          onProgress({
            type: isDownloading ? 'download' : 'init',
            progress,
            text: report.text || (isDownloading ? 'Downloading model...' : 'Initializing...')
          });
        }
      }
    });

    localEngineInstance = engine;
    localEngineReady = true;
    localEngineLoading = false;
    return wrapLocalEngine(engine);
  } catch (err) {
    localEngineLoading = false;
    localEngineReady = false;
    throw new Error(`Failed to load local AI model: ${err.message}`);
  }
}

function wrapLocalEngine(engine) {
  return {
    name: 'Gemma 3 4B (local)',
    isLocal: true,

    async text(prompt, opts = {}) {
      const response = await engine.chat.completions.create({
        messages: [{ role: 'user', content: prompt }],
        temperature: opts.temperature ?? 0.1,
        max_tokens: opts.maxTokens ?? 1024
      });
      return response.choices?.[0]?.message?.content || '';
    }
  };
}

/**
 * Check if the local model is already cached.
 */
export function isBrainReady() {
  return brainMode === 'cloud' || localEngineReady;
}

/**
 * Get model metadata for UI display.
 */
export function getBrainInfo() {
  if (brainMode === 'cloud') {
    return {
      modelId: 'groq-free',
      displayName: 'Groq Llama 4 Scout',
      downloadSizeMB: 0,
      isReady: true,
      isLoading: false,
      requiresWebGPU: false,
      isLocal: false
    };
  }
  return {
    modelId: 'gemma-3-4b-it-q4f16_1-MLC',
    displayName: 'Gemma 3 4B',
    downloadSizeMB: 2600,
    isReady: localEngineReady,
    isLoading: localEngineLoading,
    requiresWebGPU: true,
    isLocal: true
  };
}
